import { APP_ROUTES } from '@/common/constants'
import { AccountType, type Account } from '@/common/types'
import type { AccountTabKey, EditViewKey } from '@/account/account-page/types'

export type AccountRouteMode = 'adviser-full' | 'client-shell'

export const isRouteWithChildren = (pathname: string, route: string): boolean => {
    return pathname === route || pathname.startsWith(`${route}/`)
}

export const isClientsRoute = (pathname: string): boolean => isRouteWithChildren(pathname, APP_ROUTES.CLIENTS)

export const isAccountRoute = (pathname: string): boolean => isRouteWithChildren(pathname, APP_ROUTES.ACCOUNT)

export const isAdviserAccountRoute = (pathname: string): boolean => isAccountRoute(pathname)

export const isAdviserWorkspaceRoute = (pathname: string): boolean =>
    isClientsRoute(pathname) || isAdviserAccountRoute(pathname)

export const getAccountHomeRoute = (accountType: AccountType): string =>
    accountType === AccountType.ADVISER ? APP_ROUTES.CLIENTS : APP_ROUTES.DASHBOARD

export const resolveHomeRouteForContext = ({
    primaryAccount,
    activeAccountId,
}: {
    primaryAccount: Account | null
    activeAccountId: string | null
}): string => {
    if (primaryAccount?.accountType !== AccountType.ADVISER) {
        return APP_ROUTES.DASHBOARD
    }

    const isPrimaryAdviserContext = !activeAccountId || activeAccountId === primaryAccount.accountId
    return isPrimaryAdviserContext ? APP_ROUTES.CLIENTS : APP_ROUTES.DASHBOARD
}

/**
 * Returns true when the given account context represents the primary adviser being active.
 * Primary adviser context means there is a `primaryAccount` of type ADVISER and either
 * there is no `activeAccountId` (implicitly primary) or the `activeAccountId` equals the primary account id.
 */
export const isPrimaryAdviserContext = ({
    primaryAccount,
    activeAccountId,
}: {
    primaryAccount: Account | null
    activeAccountId: string | null
}): boolean => {
    if (!primaryAccount) return false
    if (primaryAccount.accountType !== AccountType.ADVISER) return false
    return !activeAccountId || activeAccountId === primaryAccount.accountId
}

export const getAccountRouteMode = ({
    pathname,
    primaryAccount,
    activeAccountId,
}: {
    pathname: string
    primaryAccount: Account | null
    activeAccountId: string | null
}): AccountRouteMode | null => {
    if (!isAccountRoute(pathname)) return null

    return isPrimaryAdviserContext({ primaryAccount, activeAccountId }) ? 'adviser-full' : 'client-shell'
}

export const isClientsRouteAllowed = ({
    pathname,
    primaryAccount,
    activeAccountId,
}: {
    pathname: string
    primaryAccount: Account | null
    activeAccountId: string | null
}): boolean => {
    if (!isClientsRoute(pathname)) return false

    return isPrimaryAdviserContext({ primaryAccount, activeAccountId })
}

/**
 * Returns true if the provided pathname is an adviser workspace route and the
 * current account context is the primary adviser context. This should be used
 * by route guards and providers to *enforce* adviser-only access instead of
 * relying on pathname checks alone.
 */
export const isAdviserRouteAllowed = ({
    pathname,
    primaryAccount,
    activeAccountId,
}: {
    pathname: string
    primaryAccount: Account | null
    activeAccountId: string | null
}): boolean => {
    if (isClientsRouteAllowed({ pathname, primaryAccount, activeAccountId })) {
        return true
    }

    if (!isAccountRoute(pathname)) return false

    return getAccountRouteMode({ pathname, primaryAccount, activeAccountId }) === 'adviser-full'
}

export const resolveNormalizedHomeRoute = ({
    pathname,
    primaryAccount,
    activeAccountId,
}: {
    pathname: string
    primaryAccount: Account | null
    activeAccountId: string | null
}): string | null => {
    if (pathname !== APP_ROUTES.DASHBOARD) return null

    const homeRoute = resolveHomeRouteForContext({ primaryAccount, activeAccountId })
    return homeRoute === APP_ROUTES.CLIENTS ? homeRoute : null
}

// Account page tab detection
export function getAccountPageTabFromPathname(pathname: string): AccountTabKey {
    if (pathname === APP_ROUTES.ACCOUNT_SETTINGS) return 'settings'
    if (pathname === APP_ROUTES.ACCOUNT_HOLDERS) return 'holders'
    return 'account'
}

// Account page edit view detection
export function getAccountPageEditViewFromPathname(pathname: string): EditViewKey | null {
    if (pathname === APP_ROUTES.ACCOUNT_TRADE_CONFIRMATION) return 'trade-confirmation'
    if (pathname === APP_ROUTES.ACCOUNT_REGISTRATION_ADDRESS) return 'registration-address'
    if (pathname === APP_ROUTES.ACCOUNT_TFN) return 'tfn'
    return null
}

// Account page menu route detection
export function isAccountPageMenuRoute(pathname: string): boolean {
    return pathname === APP_ROUTES.ACCOUNT
}

// Account page edit view active detection
export function isAccountPageEditingRoute(pathname: string): boolean {
    return getAccountPageEditViewFromPathname(pathname) !== null
}

// Get route for edit view
export function getAccountPageEditViewRoute(editViewKey: EditViewKey): string {
    if (editViewKey === 'trade-confirmation') return APP_ROUTES.ACCOUNT_TRADE_CONFIRMATION
    if (editViewKey === 'registration-address') return APP_ROUTES.ACCOUNT_REGISTRATION_ADDRESS
    if (editViewKey === 'tfn') return APP_ROUTES.ACCOUNT_TFN
    return APP_ROUTES.ACCOUNT_DETAILS
}

// Routes that require AccountProvider (account data must be loaded)
export const ACCOUNT_ROUTE_SET: ReadonlySet<string> = new Set([
    APP_ROUTES.TERMS_AND_CONDITIONS,
    APP_ROUTES.KYC_INIT,
    APP_ROUTES.KYC,
    APP_ROUTES.BANK_ACCOUNT,
    APP_ROUTES.ACCOUNT_CONFIRMATION,
    APP_ROUTES.ACCOUNT_CREATED,
    APP_ROUTES.APPLICATION_KYC,
])

export const routeNeedsAccounts = (pathname: string): boolean =>
    Array.from(ACCOUNT_ROUTE_SET).some((route) => isRouteWithChildren(pathname, route))
